import useAuth from 'client/utils/auth/useAuth';
import PlatformNotices from './PlatformNotices/PlatformNotices';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import WorkspaceTile from 'client/pages/HomePage/Workspace/WorkspaceTile/WorkspaceTile';
import Icon from '@mdi/react';
import Button from 'client/components/aria/Button/Button';

import {
    mdiAccountLock,
    mdiClipboardTextSearch,
    mdiEarthBox,
    mdiFileDocumentOutline,
    mdiHelp,
    mdiInformation,
    mdiMapSearch,
    mdiNewspaperVariantOutline,
    mdiNotebookMultiple
} from '@mdi/js';

import {
    BOOKMARKS_PAGE,
    HELP_PAGE,
    ENQUIRIES_PAGE,
    MAP_PAGE,
    NETWORK_COVERAGE_PAGE,
    NEWS_PAGE,
    OBSERVATIONS_PAGE,
    WORKPACKS_PAGE
} from 'client/utils/routes/routeConstants';
import { permissions } from 'client/utils/constants/permissions';
import { adminPortalBaseUrl, enquiryFeatureFlag } from 'client/utils/constants/frontEndConfig';

import styles from './Workspace.module.scss';
import WorkspaceSearch from './WorkspaceSearch/WorkspaceSearch';
import { WorkspaceStartIcon } from './WorkspaceStartIcon';

const Workspace = () => {
    const { permissions: userPermissions, isAuthenticated } = useAuth();
    const [mapSearchTerm, setMapSearchTerm] = useState();
    const navigate = useNavigate();
    const intl = useIntl();

    const largeTiles = [
        {
            id: 'workspace.platform-notices',
            variant: 'info',
            icon: mdiInformation,
            children: <PlatformNotices />
        },
        {
            id: 'workspace.map.search',
            icon: mdiMapSearch,
            variant: 'search',
            action: <WorkspaceSearch
                searchDisabled = { !mapSearchTerm || mapSearchTerm.trim().length === 0 }
                onClick={ () => { navigate(MAP_PAGE, { state: { searchTerm: mapSearchTerm } }); }}
                onChange={ setMapSearchTerm }
                aria-label={intl.formatMessage({ id: 'workspace.map.search.button' })}
            />,
            permission: permissions.MAP_VIEW
        }
    ];

    const regularTiles = [
        {
            id: 'workspace.admin',
            icon: mdiAccountLock,
            action: <Button
                className={styles.button}
                data-testid="workspace.admin.button"
                href={adminPortalBaseUrl}
                aria-label={intl.formatMessage({ id: 'workspace.admin.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>,
            permission: permissions.ADMIN_PORTAL_VIEW
        },
        {
            id: 'workspace.reports',
            icon: mdiAccountLock,
            action: <Button
                className={styles.button}
                data-testid="workspace.reports.button"
                href={adminPortalBaseUrl}
                aria-label={intl.formatMessage({ id: 'workspace.reports.button' })}
            >
                <WorkspaceStartIcon />
            </Button>,
            permission: permissions.REPORTS_VIEW
        },
        ...(enquiryFeatureFlag
            ? [{
                id: 'workspace.enquiries',
                icon: mdiFileDocumentOutline,
                action: <Button
                    className={styles.button}
                    data-testid="workspace.enquiries.button"
                    to={ENQUIRIES_PAGE}
                    aria-label={intl.formatMessage({ id: 'workspace.enquiries.button' })}
                >
                    <WorkspaceStartIcon/>
                </Button>,
                permission: permissions.ENQUIRY_VIEW
            }]
            : []),
        {
            id: 'workspace.workpack',
            icon: mdiFileDocumentOutline,
            action: <Button
                className={styles.button}
                data-testid="workspace.workpack.button"
                to={WORKPACKS_PAGE}
                aria-label={intl.formatMessage({ id: 'workspace.workpack.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>,
            permission: permissions.I2D_VIEW
        },
        {
            id: 'workspace.bookmarks',
            icon: mdiNotebookMultiple,
            action: <Button
                className={styles.button}
                data-testid="workspace.bookmarks.button"
                to={BOOKMARKS_PAGE}
                aria-label={intl.formatMessage({ id: 'workspace.bookmarks.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>,
            permission: permissions.MAP_VIEW
        },
        {
            id: 'workspace.observations',
            icon: mdiClipboardTextSearch,
            action: <Button
                className={styles.button}
                data-testid="workspace.observations.button"
                to={OBSERVATIONS_PAGE}
                aria-label={intl.formatMessage({ id: 'workspace.observations.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>,
            permission: permissions.OBSERVATIONS_VIEW
        },
        {
            id: 'workspace.network.coverage',
            icon: mdiEarthBox,
            action: <Button
                className={styles.button}
                data-testid="workspace.network.coverage.button"
                to={NETWORK_COVERAGE_PAGE}
                aria-label={intl.formatMessage({ id: 'workspace.network.coverage.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>
        },
        {
            id: 'workspace.help',
            icon: mdiHelp,
            action: <Button
                className={styles.button}
                data-testid="workspace.help.button"
                to={HELP_PAGE}
                aria-label={intl.formatMessage({ id: 'workspace.help.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>
        }
    ];

    const comingSoonTiles = [
        {
            id: 'workspace.news',
            icon: mdiNewspaperVariantOutline,
            action: <Button
                className={styles.button}
                data-testid="workspace.news.button"
                to={NEWS_PAGE}
                aria-label={intl.formatMessage({ id: 'workspace.news.button' })}
            >
                <WorkspaceStartIcon/>
            </Button>
        }
    ];

    const generateTiles = (tiles) => tiles.map(tile => {
        if (!tile?.permission || userPermissions.includes(tile?.permission)) {
            return <WorkspaceTile
                key={tile.id}
                data-testid={tile.id}
                variant={tile.variant}
                header={intl.formatMessage({ id: tile.id + '.header' })}
                icon={<Icon path={tile.icon} size={1} />}
                action={tile.action}
            >
                <div className="govuk-body-s" data-testid={tile.id + '.description' }>
                    {tile.children ?? <FormattedMessage id={tile.id + '.description'} />}
                </div>
            </WorkspaceTile>;
        }
        return null;
    });

    // eslint-disable-next-line multiline-ternary
    return (isAuthenticated
        ? <>
            <div data-testid="workspace">
                <div className={[styles.container, styles.largeTile].join(' ')}>
                    {generateTiles(largeTiles)}
                </div>
                <div className={styles.container}>
                    {generateTiles(regularTiles)}
                </div>
                <h2>{intl.formatMessage({ id: 'workspace.coming.soon.header' })}</h2>
                <div className={styles.container}>
                    {generateTiles(comingSoonTiles)}
                </div>
            </div>
        </>
        : null
    );
};

export default Workspace;
